import type {
  ClientOptions,
  ClipboardRender,
  ComplimentaryCareEnrollment,
  CreateDraftResponse,
  CtResourceLink,
  CtResourceSectionId,
  CtInterviewForm,
  CtInterviewSubmissionDetail,
  CtInterviewSubmissionReceipt,
  CtInterviewSubmissionSummary,
  FormSchema,
  FormValues,
  ObservationAgreementDocument,
  ObservationAgreementSignatureDetail,
  ObservationAgreementSignatureReceipt,
  ObservationAgreementSignatureSummary,
  PreviewResponse,
  Recipient,
  Referral,
  ReferralJobTitle,
  ReferralPayoutDate,
  ReferralSettings,
  ReferralWorker,
  SaveReferralJobTitleRequest,
  SaveReferralPayoutDateRequest,
  SaveCtResourceRequest,
  SaveReferralRequest,
  SectionAccessResponse,
  SignedInUser,
  TemplateSummary,
  ValidationIssue,
} from './types';

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/** A failed API call, carrying enough detail for the UI to offer the right next step. */
export class ApiError extends Error {
  readonly status: number;
  readonly issues: ValidationIssue[];
  readonly fieldErrors: Record<string, string[]>;

  /** The draft has warnings the sender must confirm before it is created. */
  readonly requiresAcknowledgement: boolean;

  /** The user needs to grant extra Graph consent; the SPA can prompt for it. */
  readonly consentRequired: boolean;
  readonly consentScopes: string[];

  constructor(
    status: number,
    message: string,
    options: {
      issues?: ValidationIssue[];
      fieldErrors?: Record<string, string[]>;
      requiresAcknowledgement?: boolean;
      consentRequired?: boolean;
      consentScopes?: string[];
    } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.issues = options.issues ?? [];
    this.fieldErrors = options.fieldErrors ?? {};
    this.requiresAcknowledgement = options.requiresAcknowledgement ?? false;
    this.consentRequired = options.consentRequired ?? false;
    this.consentScopes = options.consentScopes ?? [];
  }
}

/**
 * Supplies the bearer token for a request, or null when the API expects none.
 *
 * An injected function rather than an MSAL dependency, so the client works unchanged in the
 * credential-free local development mode — where the API authenticates every request as a fixed test
 * user and there is no token to send.
 */
export type AccessTokenProvider = () => Promise<string | null>;

export class ApiClient {
  // Explicit field rather than a constructor parameter property: the project builds with
  // `erasableSyntaxOnly`, which rules out TypeScript-only syntax that emits runtime code.
  private readonly getAccessToken: AccessTokenProvider;

  constructor(getAccessToken: AccessTokenProvider) {
    this.getAccessToken = getAccessToken;
  }

  listTemplates(signal?: AbortSignal): Promise<TemplateSummary[]> {
    return this.send('GET', '/api/templates', undefined, signal);
  }

  getFormSchema(templateId: string, signal?: AbortSignal): Promise<FormSchema> {
    return this.send('GET', `/api/templates/${encodeURIComponent(templateId)}`, undefined, signal);
  }

  getSignedInUser(signal?: AbortSignal): Promise<SignedInUser> {
    return this.send('GET', '/api/me', undefined, signal);
  }

  getClientOptions(signal?: AbortSignal): Promise<ClientOptions> {
    return this.send('GET', '/api/client-options', undefined, signal);
  }

  /** Whether the signed-in user can see and use the given section — advisory, drives the sidebar. */
  getSectionAccess(sectionId: string, signal?: AbortSignal): Promise<SectionAccessResponse> {
    return this.send('GET', `/api/${encodeURIComponent(sectionId)}/access`, undefined, signal);
  }

  /**
   * One reference-document section's links and where they currently point — `ct-resources` or
   * `ct-interview-materials`, which share one API shape.
   */
  async listCtResourceLinks(sectionId: CtResourceSectionId, signal?: AbortSignal): Promise<CtResourceLink[]> {
    const response = await this.send<{ links: CtResourceLink[] }>(
      'GET',
      `/api/${sectionId}/links`,
      undefined,
      signal,
    );

    return response.links;
  }

  /** One uploaded Clinical Team PDF, as a blob for the preview — same bearer-token reason as the documents above. */
  downloadCtResourceDocument(
    sectionId: CtResourceSectionId,
    id: string,
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; filename: string }> {
    return this.sendBinary(`/api/${sectionId}/documents/${encodeURIComponent(id)}`, signal);
  }

  /** Adds an entry — a PDF, a link, or both — to the end of one reference page. */
  createCtResource(
    sectionId: CtResourceSectionId,
    request: SaveCtResourceRequest,
    signal?: AbortSignal,
  ): Promise<CtResourceLink> {
    return this.sendForm('POST', `/api/${sectionId}/links`, ctResourceForm(request), signal);
  }

  /** Changes an entry. A file replaces its PDF; none keeps it, unless `removeDocument` says otherwise. */
  updateCtResource(
    sectionId: CtResourceSectionId,
    id: string,
    request: SaveCtResourceRequest,
    signal?: AbortSignal,
  ): Promise<CtResourceLink> {
    return this.sendForm('PUT', `/api/${sectionId}/links/${encodeURIComponent(id)}`, ctResourceForm(request), signal);
  }

  /** Removes an entry and its PDF. */
  deleteCtResource(sectionId: CtResourceSectionId, id: string, signal?: AbortSignal): Promise<void> {
    return this.send('DELETE', `/api/${sectionId}/links/${encodeURIComponent(id)}`, undefined, signal);
  }

  preview(templateId: string, values: FormValues, signal?: AbortSignal): Promise<PreviewResponse> {
    return this.send(
      'POST',
      `/api/templates/${encodeURIComponent(templateId)}/preview`,
      { values },
      signal,
    );
  }

  /** Renders the email as self-contained HTML for the clipboard. Refused while anything required is empty. */
  renderForClipboard(templateId: string, values: FormValues, signal?: AbortSignal): Promise<ClipboardRender> {
    return this.send(
      'POST',
      `/api/templates/${encodeURIComponent(templateId)}/clipboard`,
      { values },
      signal,
    );
  }

  createDraft(
    templateId: string,
    request: {
      values: FormValues;
      to: Recipient[];
      acknowledgeWarnings: boolean;
    },
    signal?: AbortSignal,
  ): Promise<CreateDraftResponse> {
    return this.send(
      'POST',
      `/api/templates/${encodeURIComponent(templateId)}/draft`,
      request,
      signal,
    );
  }

  /* ------------------------------------------------------------- employee referral */

  listReferrals(signal?: AbortSignal): Promise<{ referrals: Referral[] }> {
    return this.send('GET', '/api/employee-referral/referrals', undefined, signal);
  }

  createReferral(request: SaveReferralRequest, signal?: AbortSignal): Promise<Referral> {
    return this.send('POST', '/api/employee-referral/referrals', request, signal);
  }

  updateReferral(id: string, request: SaveReferralRequest, signal?: AbortSignal): Promise<Referral> {
    return this.send('PUT', `/api/employee-referral/referrals/${encodeURIComponent(id)}`, request, signal);
  }

  /** HR's accept/turn-down decision on one referral. `null` puts it back to undecided. */
  setReferralEligibility(
    id: string,
    eligibleForPayout: boolean | null,
    signal?: AbortSignal,
  ): Promise<Referral> {
    return this.send(
      'POST',
      `/api/employee-referral/referrals/${encodeURIComponent(id)}/eligibility`,
      { eligibleForPayout },
      signal,
    );
  }

  /**
   * Closes one referral out — paid or not paid, finished either way. The whole of what the Closure
   * list does, in one call with the answer in it.
   */
  closeReferral(id: string, paid: boolean, signal?: AbortSignal): Promise<Referral> {
    return this.send(
      'POST',
      `/api/employee-referral/referrals/${encodeURIComponent(id)}/close`,
      { paid },
      signal,
    );
  }

  /** Unlocks a finished referral so its decision and statuses can be changed again. */
  reopenReferral(id: string, signal?: AbortSignal): Promise<Referral> {
    return this.send(
      'POST',
      `/api/employee-referral/referrals/${encodeURIComponent(id)}/reopen`,
      undefined,
      signal,
    );
  }

  // --- Complimentary chiropractic care enrollment ---

  submitComplimentaryCareEnrollment(form: FormData, signal?: AbortSignal): Promise<ComplimentaryCareEnrollment> {
    return this.sendForm('POST', '/api/complimentary-care/enrollments', form, signal);
  }

  /**
   * The signed-in employee's enrollment and the change they have proposed to it, either of which can
   * be null. Two fields because "what am I enrolled for" and "what did I last send in" have different
   * answers while a change is under review or after one was turned down.
   */
  getMyComplimentaryCareEnrollment(signal?: AbortSignal): Promise<{
    enrollment: ComplimentaryCareEnrollment | null;
    proposedEdit: ComplimentaryCareEnrollment | null;
  }> {
    return this.send('GET', '/api/complimentary-care/enrollments/mine', undefined, signal);
  }

  /**
   * The clinics both dropdowns on the enrollment form offer, by name. An empty list means the
   * directory could not be read — the form says so rather than showing no choices.
   */
  async listComplimentaryCareClinics(signal?: AbortSignal): Promise<string[]> {
    const response = await this.send<{ clinics: string[] }>(
      'GET',
      '/api/complimentary-care/clinics',
      undefined,
      signal,
    );
    return response.clinics;
  }

  getComplimentaryCareEnrollment(id: string, signal?: AbortSignal): Promise<ComplimentaryCareEnrollment> {
    return this.send('GET', `/api/complimentary-care-review/enrollments/${encodeURIComponent(id)}`, undefined, signal);
  }

  async listComplimentaryCareEnrollments(signal?: AbortSignal): Promise<ComplimentaryCareEnrollment[]> {
    const response = await this.send<{ enrollments: ComplimentaryCareEnrollment[] }>(
      'GET',
      '/api/complimentary-care-review/enrollments',
      undefined,
      signal,
    );
    return response.enrollments;
  }

  /** One call for every HR decision — the action is the last path segment, the note travels with it. */
  reviewComplimentaryCareEnrollment(
    id: string,
    action: 'approve' | 'reject' | 'deactivate' | 'reactivate',
    note?: string,
    signal?: AbortSignal,
  ): Promise<ComplimentaryCareEnrollment> {
    return this.send(
      'POST',
      `/api/complimentary-care-review/enrollments/${encodeURIComponent(id)}/${action}`,
      { note: note ?? null },
      signal,
    );
  }

  /**
   * Replaces HR's own note about the employee this enrollment belongs to. An empty string clears it.
   * Returns the enrollment again so the caller picks up who saved it and when without a second read.
   */
  saveComplimentaryCareHrNote(
    enrollmentId: string,
    note: string,
    signal?: AbortSignal,
  ): Promise<ComplimentaryCareEnrollment> {
    return this.send(
      'PUT',
      `/api/complimentary-care-review/enrollments/${encodeURIComponent(enrollmentId)}/hr-note`,
      { note },
      signal,
    );
  }

  /**
   * Closes out a referral whose payout date went by with nobody confirming it. `paid` true records
   * the payment that did happen; false pins it to the next payroll run.
   */
  resolveMissedPayout(id: string, paid: boolean, signal?: AbortSignal): Promise<Referral> {
    return this.send(
      'POST',
      `/api/employee-referral/referrals/${encodeURIComponent(id)}/resolve-missed`,
      { paid },
      signal,
    );
  }

  /**
   * Fetches one enrollee's document as a blob rather than handing back a URL: the download needs the
   * same bearer token as every other call, which a plain `<a href>` to the API can't carry.
   */
  downloadEnrolleeDocument(
    enrollmentId: string,
    enrolleeId: string,
    signal?: AbortSignal,
  ): Promise<{ blob: Blob; filename: string }> {
    return this.sendBinary(
      `/api/complimentary-care-review/enrollments/${encodeURIComponent(enrollmentId)}/enrollees/${encodeURIComponent(enrolleeId)}/document`,
      signal,
    );
  }

  /**
   * The same file from the employee's own side of the form. A separate route rather than the review
   * one with a different id, because an employee is not in HR's group — and this one can only ever
   * reach their own enrollment.
   */
  downloadMyEnrolleeDocument(enrolleeId: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
    return this.sendBinary(
      `/api/complimentary-care/enrollments/mine/enrollees/${encodeURIComponent(enrolleeId)}/document`,
      signal,
    );
  }

  /** The schedule behind the computed 90-day and payout dates. */
  getReferralSettings(signal?: AbortSignal): Promise<ReferralSettings> {
    return this.send('GET', '/api/employee-referral/settings', undefined, signal);
  }

  listReferralJobTitles(signal?: AbortSignal): Promise<{ jobTitles: ReferralJobTitle[] }> {
    return this.send('GET', '/api/employee-referral/job-titles', undefined, signal);
  }

  createReferralJobTitle(
    request: SaveReferralJobTitleRequest,
    signal?: AbortSignal,
  ): Promise<ReferralJobTitle> {
    return this.send('POST', '/api/employee-referral/job-titles', request, signal);
  }

  updateReferralJobTitle(
    id: string,
    request: SaveReferralJobTitleRequest,
    signal?: AbortSignal,
  ): Promise<ReferralJobTitle> {
    return this.send('PUT', `/api/employee-referral/job-titles/${encodeURIComponent(id)}`, request, signal);
  }

  /** The payroll calendar a referral's payout date is looked up in. */
  listReferralPayoutDates(signal?: AbortSignal): Promise<{ payoutDates: ReferralPayoutDate[] }> {
    return this.send('GET', '/api/employee-referral/payout-dates', undefined, signal);
  }

  /** Everyone on the payroll, to pick the referring team member from. */
  listReferralWorkers(signal?: AbortSignal): Promise<{ workers: ReferralWorker[] }> {
    return this.send('GET', '/api/employee-referral/workers', undefined, signal);
  }

  createReferralPayoutDate(
    request: SaveReferralPayoutDateRequest,
    signal?: AbortSignal,
  ): Promise<ReferralPayoutDate> {
    return this.send('POST', '/api/employee-referral/payout-dates', request, signal);
  }

  updateReferralPayoutDate(
    id: string,
    request: SaveReferralPayoutDateRequest,
    signal?: AbortSignal,
  ): Promise<ReferralPayoutDate> {
    return this.send('PUT', `/api/employee-referral/payout-dates/${encodeURIComponent(id)}`, request, signal);
  }

  deleteReferralPayoutDate(id: string, signal?: AbortSignal): Promise<void> {
    return this.send('DELETE', `/api/employee-referral/payout-dates/${encodeURIComponent(id)}`, undefined, signal);
  }

  // --- Public forms (no token: the CT assessment, the observation agreement, and what they share) ---

  /**
   * The clinics both public forms offer in their location dropdown, by name. An empty list means the
   * directory could not be read — the forms fall back to a text box rather than show no choices.
   */
  async listPublicClinics(signal?: AbortSignal): Promise<string[]> {
    const response = await this.send<{ clinics: string[] }>('GET', '/api/public/clinics', undefined, signal);
    return response.clinics;
  }

  // --- CT interview (public: these two are the only calls that work without a token) ---

  getCtInterviewForm(signal?: AbortSignal): Promise<CtInterviewForm> {
    return this.send('GET', '/api/public/ct-interview/form', undefined, signal);
  }

  submitCtInterview(
    request: {
      candidateName: string;
      location: string;
      formVersion: number;

      /** Answer text keyed by question id. A skipped question may be absent or blank. */
      answers: Record<string, string>;
    },
    signal?: AbortSignal,
  ): Promise<CtInterviewSubmissionReceipt> {
    return this.send('POST', '/api/public/ct-interview/submissions', request, signal);
  }

  // --- CT interview review (signed in, HR group) ---

  async listCtInterviewSubmissions(signal?: AbortSignal): Promise<CtInterviewSubmissionSummary[]> {
    const response = await this.send<{ submissions: CtInterviewSubmissionSummary[] }>(
      'GET',
      '/api/ct-interview-review/submissions',
      undefined,
      signal,
    );
    return response.submissions;
  }

  getCtInterviewSubmission(id: string, signal?: AbortSignal): Promise<CtInterviewSubmissionDetail> {
    return this.send('GET', `/api/ct-interview-review/submissions/${encodeURIComponent(id)}`, undefined, signal);
  }

  // --- Observation agreement (public: these two also work without a token) ---

  getObservationAgreement(signal?: AbortSignal): Promise<ObservationAgreementDocument> {
    return this.send('GET', '/api/public/observation-agreement/form', undefined, signal);
  }

  /** Rejects with a 409 `ApiError` when the agreement was revised while the page was open. */
  signObservationAgreement(
    request: {
      signerName: string;

      /** Both optional on the form. Blank is sent as-is; the API folds it to "not given". */
      email: string;
      location: string;
      agreementVersion: number;

      /** Always true in practice — the page cannot submit without it. Sent so the API can refuse a
       *  caller that is not the page rather than record a consent nobody gave. */
      agreed: boolean;
    },
    signal?: AbortSignal,
  ): Promise<ObservationAgreementSignatureReceipt> {
    return this.send('POST', '/api/public/observation-agreement/signatures', request, signal);
  }

  // --- Observation agreement review (signed in, the section's group) ---

  async listObservationAgreementSignatures(
    signal?: AbortSignal,
  ): Promise<ObservationAgreementSignatureSummary[]> {
    const response = await this.send<{ signatures: ObservationAgreementSignatureSummary[] }>(
      'GET',
      '/api/observation-agreement-review/signatures',
      undefined,
      signal,
    );
    return response.signatures;
  }

  getObservationAgreementSignature(
    id: string,
    signal?: AbortSignal,
  ): Promise<ObservationAgreementSignatureDetail> {
    return this.send(
      'GET',
      `/api/observation-agreement-review/signatures/${encodeURIComponent(id)}`,
      undefined,
      signal,
    );
  }

  private async send<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const token = await this.getAccessToken();

    const response = await fetch(`${baseUrl}${path}`, {
      method,
      signal,
      headers: {
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.ok) {
      // A 204 carries no body, and asking for its JSON throws. Delete answers that way.
      if (response.status === 204) {
        return undefined as T;
      }

      return (await response.json()) as T;
    }

    throw await toApiError(response);
  }

  /** Same shape as {@link send}, but for a `multipart/form-data` body — no JSON stringify, and no
   *  explicit `Content-Type`, since the browser has to set that itself to include the form's boundary. */
  private async sendForm<T>(
    method: 'POST' | 'PUT',
    path: string,
    form: FormData,
    signal?: AbortSignal,
  ): Promise<T> {
    const token = await this.getAccessToken();

    const response = await fetch(`${baseUrl}${path}`, {
      method,
      signal,
      headers: {
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      },
      body: form,
    });

    if (response.ok) {
      return (await response.json()) as T;
    }

    throw await toApiError(response);
  }

  private async sendBinary(path: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
    const token = await this.getAccessToken();

    const response = await fetch(`${baseUrl}${path}`, {
      method: 'GET',
      signal,
      headers: {
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw await toApiError(response);
    }

    const blob = await response.blob();
    const filename = filenameFrom(response.headers.get('Content-Disposition')) ?? 'document';

    return { blob, filename };
  }
}

/** The multipart body the CT resources create and update endpoints read, field for field. */
function ctResourceForm(request: SaveCtResourceRequest): FormData {
  const form = new FormData();
  form.append('name', request.name);
  form.append('description', request.description);
  form.append('kind', request.kind);
  form.append('url', request.url);

  if (request.file) {
    form.append('file', request.file);
  } else if (request.removeDocument) {
    form.append('removeDocument', 'true');
  }

  return form;
}

/** Pulls the filename back out of `Content-Disposition: attachment; filename=...`. */
function filenameFrom(contentDisposition: string | null): string | null {
  if (!contentDisposition) {
    return null;
  }

  const match = /filename="?([^";]+)"?/.exec(contentDisposition);
  return match ? match[1] : null;
}

/** Turns an RFC 9457 problem response into an ApiError, tolerating non-JSON bodies. */
async function toApiError(response: Response): Promise<ApiError> {
  let problem: Record<string, unknown> = {};

  try {
    problem = (await response.json()) as Record<string, unknown>;
  } catch {
    return new ApiError(response.status, `Request failed with ${response.status}.`);
  }

  const title = typeof problem.title === 'string' ? problem.title : `Request failed with ${response.status}.`;
  const detail = typeof problem.detail === 'string' ? problem.detail : '';

  // ASP.NET Core validation problems put per-field messages under "errors".
  const fieldErrors =
    problem.errors && typeof problem.errors === 'object'
      ? (problem.errors as Record<string, string[]>)
      : {};

  return new ApiError(response.status, detail ? `${title} ${detail}` : title, {
    issues: Array.isArray(problem.issues) ? (problem.issues as ValidationIssue[]) : [],
    fieldErrors,
    requiresAcknowledgement: problem.requiresAcknowledgement === true,
    consentRequired: problem.consentRequired === true,
    consentScopes: Array.isArray(problem.scopes) ? (problem.scopes as string[]) : [],
  });
}
